import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    Button,
    Container,
    useColorModeValue,
    Card,
    CardBody,
    Spinner,
    Alert,
    Image,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    HStack,
    Icon,
    Divider,
    Badge
} from '@chakra-ui/react';
import { useMsal } from '@azure/msal-react';
import { MdEmail, MdSecurity, MdCloud, MdRefresh } from 'react-icons/md';
import { FaMicrosoft } from 'react-icons/fa';
import { AuthService } from '../../services/authService';
import { formatDateTime } from '../../utils/helpers';
import toast from 'react-hot-toast';

/**
 * Microsoft SSO Login Page Component
 */
function LoginPage({ appConfig, onError, authServiceReady, userStatus }) {
    const { inProgress } = useMsal();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [systemInfo, setSystemInfo] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [sessionValidation, setSessionValidation] = useState(null);

    // Theme-aware colors for cross-platform compatibility
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    /**
     * Initialize login page with system information
     * Loads application configuration and displays login interface
     * Performs session validation and cleanup if needed
     */
    const initializeLoginPage = useCallback(async () => {
        try {
            // Only set system info if it hasn't been set or config changed
            if (!systemInfo || (appConfig && systemInfo.appName !== appConfig?.app?.name)) {
                const systemConfig = {
                    appName: appConfig?.app?.name || 'Prism Monitor',
                    version: appConfig?.app?.version || '1.0.0',
                    environment: appConfig?.app?.environment || process.env.NODE_ENV || 'production',
                    buildTime: appConfig?.app?.buildTime || new Date().toISOString()
                };
                
                setSystemInfo(systemConfig);
            }

            // Validate existing session and cleanup if invalid (only if authService is ready)
            if (authServiceReady) {
                const sessionInfo = AuthService.validateSession();
                setSessionValidation(sessionInfo);
                
                if (sessionInfo && !sessionInfo.valid && sessionInfo.issues.length > 0) {
                    AuthService.clearUserSession();
                    
                    // Clear local storage if session is corrupted
                    if (sessionInfo.issues.includes('Authenticated but no MSAL account')) {
                        localStorage.clear();
                        toast('Previous session cleaned up', {
                            icon: '🔄',
                            duration: 2000,
                            position: 'bottom-center'
                        });
                    }
                }
            }

            // Clear any existing error states
            setError(null);
            setRetryCount(0);

        } catch (error) {
            console.error('Login page initialization failed:', {
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            });
            setError('Failed to initialize login page. Please refresh and try again.');
        }
    }, [authServiceReady, appConfig, systemInfo]);

    /**
     * Initialize login page and load system information
     * Sets up the login interface and displays application details
     * Validates authentication service readiness
     */
    useEffect(() => {
        initializeLoginPage();
    }, [initializeLoginPage]);

    /**
     * Handle Microsoft SSO login initiation
     * Triggers silent SSO with redirect fallback for mobile compatibility
     */
    const handleLogin = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Validate authentication service readiness
            if (!authServiceReady) {
                throw new Error('Authentication system is not ready. Please wait and try again.');
            }

            // Check if MSAL is still in progress
            if (inProgress !== 'none') {
                toast('Authentication in progress, please wait...', {
                    icon: '⏳',
                    duration: 3000,
                    position: 'bottom-center'
                });
                return;
            }

            // Validate AuthService state before login
            const sessionInfo = AuthService.getSessionInfo();
            if (!sessionInfo.isInitialized || !sessionInfo.hasMsalInstance) {
                throw new Error('Authentication service not properly initialized');
            }

            // Initiate Microsoft SSO popup login
            const loginResponse = await AuthService.login();

            if (loginResponse) {
                // Handle successful login callback
                const result = await AuthService.handleLoginSuccess(loginResponse);
                
                if (result && result.account) {
                    toast.success('Login successful! Loading dashboard...', {
                        duration: 2000,
                        position: 'bottom-center'
                    });
                } else {
                    throw new Error('Login succeeded but no account information received');
                }
            }

        } catch (error) {
            console.error('Login initiation failed:', {
                error: error.message,
                errorCode: error.errorCode,
                stack: error.stack,
                timestamp: new Date().toISOString(),
                retryCount,
                authServiceReady,
                inProgress
            });
            
            // Handle authentication service errors specifically
            const errorMessage = error.message || error.toString();
            let displayError = 'Login failed. Please try again.';
            
            // Handle specific error types
            if (error.errorCode === 'popup_window_error' || errorMessage.includes('popup')) {
                displayError = 'Popup was blocked by your browser. Please allow popups for this site and try again.';
            } else if (error.errorCode === 'user_cancelled') {
                displayError = 'Login was cancelled. Please try again when ready.';
            } else if (errorMessage.includes('not ready')) {
                displayError = 'Authentication system is still loading. Please wait a moment and try again.';
            } else if (errorMessage.includes('in progress')) {
                displayError = 'Another authentication operation is in progress. Please wait.';
            } else if (errorMessage.includes('PENDING_APPROVAL:')) {
                displayError = errorMessage.split('PENDING_APPROVAL:')[1] || 'Your account is pending approval.';
            } else if (errorMessage.includes('ACCOUNT_REJECTED:')) {
                displayError = errorMessage.split('ACCOUNT_REJECTED:')[1] || 'Your account has been rejected.';
            } else if (errorMessage.includes('ACCOUNT_SUSPENDED:')) {
                displayError = errorMessage.split('ACCOUNT_SUSPENDED:')[1] || 'Your account has been suspended.';
            } else if (errorMessage.includes('USER_CREATION_FAILED:')) {
                displayError = errorMessage.split('USER_CREATION_FAILED:')[1] || 'Failed to create user account.';
            } else {
                displayError = errorMessage || 'Login failed. Please try again.';
            }
            
            setError(displayError);
            setRetryCount(prev => prev + 1);
            
            // Don't show toast for user cancellation or status-related errors
            if (error.errorCode !== 'user_cancelled' && 
                !errorMessage.includes('PENDING_APPROVAL:') &&
                !errorMessage.includes('ACCOUNT_REJECTED:') &&
                !errorMessage.includes('ACCOUNT_SUSPENDED:')) {
                toast.error(displayError, {
                    duration: 6000,
                    position: 'bottom-center'
                });
            }
            
            // Report error to parent component
            onError?.(error);
            
            // Auto-retry logic for transient errors only
            if (retryCount < 2 && (
                errorMessage.includes('not ready') || 
                errorMessage.includes('in progress')
            )) {
                setTimeout(() => {
                    handleLogin();
                }, 2000);
            }
            
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handle retry action for failed login attempts
     * Clears error state and attempts login again
     * Implements exponential backoff for repeated failures
     */
    const handleRetry = () => {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Max 5 second delay
        
        setError(null);
        
        if (delay > 1000) {
            toast(`Retrying in ${delay / 1000} seconds...`, {
                icon: '🔄',
                duration: delay,
                position: 'bottom-center'
            });
            
            setTimeout(() => {
                handleLogin();
            }, delay);
        } else {
            handleLogin();
        }
    };

    /**
     * Handle system refresh action
     * Reloads the page to reset authentication state
     * Used for recovery from persistent errors
     */
    const handleSystemRefresh = () => {
        toast('Refreshing application...', {
            icon: '🔄',
            duration: 2000,
            position: 'bottom-center'
        });
        
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    };

    /**
     * Get status-specific styling and content
     * Returns appropriate colors and messages based on user status
     */
    const getStatusInfo = () => {
        switch (userStatus) {
            case 'pending':
                return {
                    color: 'orange',
                    icon: '⏳',
                    title: 'Account Pending Approval',
                    message: 'Your account is pending administrator approval. Please wait for approval before accessing the dashboard.',
                    showLoginButton: false
                };
            case 'rejected':
                return {
                    color: 'red',
                    icon: '❌',
                    title: 'Account Rejected',
                    message: 'Your account has been rejected. Please contact the administrator for assistance.',
                    showLoginButton: false
                };
            case 'suspended':
                return {
                    color: 'red',
                    icon: '🚫',
                    title: 'Account Suspended',
                    message: 'Your account has been suspended. Please contact the administrator for assistance.',
                    showLoginButton: false
                };
            case 'error':
                return {
                    color: 'red',
                    icon: '⚠️',
                    title: 'Account Error',
                    message: 'There was an error with your account. Please contact the administrator for assistance.',
                    showLoginButton: false
                };
            default:
                return {
                    color: 'blue',
                    icon: '🔐',
                    title: 'Sign in to continue',
                    message: 'Use your Microsoft account to access the dashboard',
                    showLoginButton: true
                };
        }
    };

    /**
     * Feature highlight component
     * Displays key features of the Email Dashboard application
     * Implements responsive design with icon and description
     */
    const FeatureHighlight = ({ icon, title, description }) => (
        <HStack spacing={3} align="start">
            <Icon as={icon} w={6} h={6} color="brand.500" mt={1} />
            <Box>
                <Text fontWeight="semibold" fontSize="sm" mb={1}>
                    {title}
                </Text>
                <Text fontSize="xs" color={textColor}>
                    {description}
                </Text>
            </Box>
        </HStack>
    );

    const statusInfo = getStatusInfo();

    return (
        <Box minH="100vh" bg={bgColor} py={12}>
            <Container maxW="md" centerContent>
                <VStack spacing={8} w="full">
                    {/* Application Header */}
                    <VStack spacing={4} textAlign="center">
                        <Image  src="prismicon96.png"  alt="Prism Monitor"  height="60px"  width="auto" paddingRight={2} />
                        <VStack spacing={2}>
                            <Heading 
                                size="xl" 
                                color={headingColor}
                                fontWeight="bold"
                            >
                                {systemInfo?.appName || 'Prism Monitor'}
                            </Heading>
                            <Text fontSize="lg" color={textColor}>
                                Analytics Dashboard Interface
                            </Text>
                            {systemInfo?.version && (
                                <HStack spacing={2}>
                                    <Text fontSize="sm" color={textColor} opacity={0.7}>
                                        Version {systemInfo.version}
                                    </Text>
                                    {systemInfo?.environment === 'development' && (
                                        <Badge colorScheme="orange" size="sm">
                                            DEV
                                        </Badge>
                                    )}
                                </HStack>
                            )}
                        </VStack>
                    </VStack>


                    {/* Login Card */}
                    <Card w="full" bg={cardBg} shadow="xl" borderColor={borderColor}>
                        <CardBody p={8}>
                            <VStack spacing={6}>
                                {/* Login Header */}
                                <VStack spacing={2} textAlign="center">
                                    <Heading size="md" color={headingColor}>
                                        {statusInfo.title}
                                    </Heading>
                                    <Text fontSize="sm" color={textColor}>
                                        {statusInfo.message}
                                    </Text>
                                </VStack>

                                {/* Status-specific Alert */}
                                {userStatus && userStatus !== 'approved' && (
                                    <Alert status={statusInfo.color === 'orange' ? 'warning' : 'error'} borderRadius="md">
                                        <AlertIcon />
                                        <Box>
                                            <AlertTitle fontSize="sm">{statusInfo.title}</AlertTitle>
                                            <AlertDescription fontSize="sm">
                                                {statusInfo.message}
                                            </AlertDescription>
                                        </Box>
                                    </Alert>
                                )}

                                {/* Error Display */}
                                {error && (
                                    <Alert status="error" borderRadius="md">
                                        <AlertIcon />
                                        <Box>
                                            <AlertTitle fontSize="sm">Login Failed</AlertTitle>
                                            <AlertDescription fontSize="sm">
                                                {error}
                                                {retryCount > 0 && (
                                                    <Text fontSize="xs" mt={1} opacity={0.8}>
                                                        Attempt {retryCount + 1}
                                                    </Text>
                                                )}
                                            </AlertDescription>
                                        </Box>
                                    </Alert>
                                )}

                                {/* Microsoft Login Button - only show if status allows */}
                                {statusInfo.showLoginButton && (
                                    <Button
                                        size="lg"
                                        w="full"
                                        leftIcon={
                                            isLoading ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                <FaMicrosoft />
                                            )
                                        }
                                        onClick={handleLogin}
                                        isLoading={isLoading}
                                        loadingText="Redirecting..."
                                        colorScheme="blue"
                                        bg="#0078d4"
                                        _hover={{ bg: "#106ebe" }}
                                        _active={{ bg: "#005a9e" }}
                                        isDisabled={!authServiceReady || inProgress !== 'none' || isLoading}
                                    >
                                        {!authServiceReady 
                                            ? 'Initializing...' 
                                            : isLoading 
                                                ? 'Redirecting to Microsoft...' 
                                                : 'Sign in with Microsoft'
                                        }
                                    </Button>
                                )}

                                {/* Action Buttons for Errors */}
                                {error && statusInfo.showLoginButton && (
                                    <HStack spacing={2} w="full">
                                        <Button
                                            size="md"
                                            variant="outline"
                                            onClick={handleRetry}
                                            flex={1}
                                            leftIcon={<MdRefresh />}
                                            isDisabled={isLoading}
                                        >
                                            Try Again
                                        </Button>
                                        
                                        {retryCount >= 2 && (
                                            <Button
                                                size="md"
                                                variant="ghost"
                                                onClick={handleSystemRefresh}
                                                flex={1}
                                                leftIcon={<MdRefresh />}
                                            >
                                                Refresh
                                            </Button>
                                        )}
                                    </HStack>
                                )}

                                {/* Sign Out Button for status issues */}
                                {!statusInfo.showLoginButton && (userStatus === 'pending' || userStatus === 'rejected' || userStatus === 'suspended') && (
                                    <Button
                                        size="md"
                                        variant="outline"
                                        onClick={() => AuthService.logout()}
                                        w="full"
                                    >
                                        Sign Out
                                    </Button>
                                )}

                                <Divider />

                                {/* Security Note */}
                                <Box 
                                    bg={useColorModeValue('blue.50', 'blue.900')} 
                                    p={4} 
                                    borderRadius="md"
                                    w="full"
                                >
                                    <HStack spacing={3}>
                                        <Icon as={MdSecurity} color="blue.500" />
                                        <VStack align="start" spacing={1}>
                                            <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                                                Secure Authentication
                                            </Text>
                                            <Text fontSize="xs" color="blue.600">
                                                Your login is secured by Microsoft Entra ID
                                            </Text>
                                        </VStack>
                                    </HStack>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Feature Highlights */}
                    <Card w="full" bg={cardBg} borderColor={borderColor}>
                        <CardBody p={6}>
                            <VStack spacing={4}>
                                <Heading size="sm" color={headingColor} textAlign="center">
                                    Key Features
                                </Heading>
                                
                                <VStack spacing={4} w="full">
                                    <FeatureHighlight
                                        icon={MdEmail}
                                        title="Email Analytics"
                                        description="Comprehensive reporting and analysis of Bulk Mail with real-time performance metrics"
                                    />
                                    
                                    <FeatureHighlight
                                        icon={MdCloud}
                                        title="Real-time Monitoring"
                                        description="Live client heartbeat tracking with instant notification system and automated alerting"
                                    />
                                    
                                    <FeatureHighlight
                                        icon={MdSecurity}
                                        title="Secure Access"
                                        description="Role-based access control with Microsoft SSO integration and audit logging"
                                    />
                                </VStack>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Footer Information */}
                    <VStack spacing={2} textAlign="center" opacity={0.7}>
                        <Text fontSize="xs" color={textColor}>
                            {formatDateTime(new Date(), { showDate: true, showTime: false })}
                        </Text>
                        
                        {systemInfo?.environment === 'development' && (
                            <Text fontSize="xs" color="orange.500" fontWeight="semibold">
                                Development Environment
                            </Text>
                        )}
                        
                        <Text fontSize="xs" color={textColor}>
                            Prism Monitor • Analytics Dashboard Interface
                        </Text>
                    </VStack>
                </VStack>
            </Container>
        </Box>
    );
}

export default LoginPage;